<template>
    <el-dialog v-model="opened" title="绑定文本" width="70%" :before-close="close">
        <el-row :gutter="12">
            <!-- 左侧：已绑定文本 -->
            <el-col :span="12">
                <!-- 当为手办时，单独展示名称 -->
                <div v-if="parentItemType === 'figure'" style="margin-bottom: 8px">
                    <b>手办名称</b>
                    <el-card shadow="never" style="margin-top: 8px">
                        <div v-if="boundName" style="white-space: pre-wrap; max-height: 120px; overflow: auto">
                            {{ boundName.nickname || boundName.EN?.slice(0, 60) || '未命名' }} ({{ boundName.EN }})
                        </div>
                        <div v-else style="color: var(--el-text-color-secondary)">未设置名称。可在右侧搜索中使用“设为名称”。</div>
                    </el-card>
                </div>
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px">
                    <b>已绑定文本</b>
                    <el-button size="small" type="danger" :disabled="!boundSelected.length"
                        @click="unbindStories">移除选中</el-button>
                </div>
                <vue-draggable-next v-model="boundStories" item-key="id" handle=".handle" :animation="200">
                    <transition-group>
                        <el-card v-for="s in boundStories" :key="s.id" shadow="never" style="margin-bottom: 8px">
                            <template #header>
                                <div style="display: flex; justify-content: space-between; align-items: center">
                                    <div style="display: flex; align-items: center; gap: 8px">
                                        <el-button circle size="small" class="handle"><el-icon>
                                                <Sort />
                                            </el-icon></el-button>
                                        <span>{{ s.nickname || s.EN?.slice(0, 20) || '未命名' }}</span>
                                    </div>
                                    <el-checkbox v-model="boundSelected" :label="s.id"></el-checkbox>
                                </div>
                            </template>
                            <div style="white-space: pre-wrap; max-height: 120px; overflow: auto">{{ s.EN }}</div>
                        </el-card>
                    </transition-group>
                </vue-draggable-next>
                <el-empty v-if="!boundStories.length" description="暂无绑定文本"></el-empty>
            </el-col>

            <!-- 右侧：搜索添加 -->
            <el-col :span="12">
                <div style="display: flex; gap: 8px; margin-bottom: 8px">
                    <el-input v-model="keyword" placeholder="输入关键词搜索" clearable @clear="searchStories()"
                        @keyup.enter="searchStories()"></el-input>
                    <el-button type="primary" @click="searchStories()">搜索</el-button>
                </div>
                <el-table :data="candidates" v-loading=loading max-height="400">
                    <el-table-column label="内部名称">
                        <template #default="scope">
                            <div style="white-space: pre-wrap; word-break: break-word; line-height: 1.6;">
                                {{ scope.row.nickname }}
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column header-align="right" label="操作" :width="80">
                        <template #default="scope">
                            <el-button v-if="parentItemType === 'figure'" size="small" type="success" text @click="setName(scope.row)">设为名称</el-button>
                            <el-button style="margin-left: 0;" size="small" type="primary" text @click="addStory(scope.row)">添加文本</el-button>
                        </template>
                    </el-table-column>
                </el-table>
                <el-pagination layout="prev, pager, next" :total="total" :page-size="limit"
                    @current-change="searchStories" v-if="total > limit"></el-pagination>
            </el-col>
        </el-row>

        <template #footer>
            <el-button @click="close">取消</el-button>
            <el-button type="primary" @click="save">保存</el-button>
        </template>
    </el-dialog>
</template>

<script>
import axios from 'ts-axios-new';
import { VueDraggableNext } from 'vue-draggable-next';
import { ElMessage } from 'element-plus';
import { Sort } from '@element-plus/icons-vue';
import { update } from '../../libs/utils';

export default {
    name: 'CopywritingBindDialog',
    components: { VueDraggableNext, Sort },
    data() {
        return {
            opened: false,
            parentItem: null,      // 外部传入的章节/主题/手办对象
            parentItemType: null,
            boundName: null,       // 手办名称（figure 类型独有）
            boundStories: [],     // 已绑定文本（可拖动排序）
            boundSelected: [],    // 左侧选中要移除的 id
            keyword: '',
            candidates: [],
            total: 0,
            skip: 0,
            limit: 10,
            loading: false,
        };
    },
    methods: {
        // 外部调用：传入已有 stories 数组 和 父级对象（用于后续保存）
        open(parentItem) {
            this.parentItem = parentItem;
            this.parentItemType = parentItem?.__topics ? 'chapter' : parentItem?.__figures ? 'topic' : 'figure';
            this.boundName = null;
            this.boundStories = [];
            this.boundSelected = [];
            this.keyword = '';
            this.candidates = [];
            this.total = 0;
            this.skip = 0;
            this.opened = true;
            this.refresh();
        },
        close() {
            this.opened = false;
            this.parentItem = null;
            this.boundName = null;
        },
        async refresh(){
            const ids = this.parentItem?.stories;
            if (ids?.length) {
                try {
                    this.loading = true;
                    await axios.get('/cms/v1/copywriting', {
                        params: {
                            ids: ids.join(',')
                        },
                    }).then(res => {
                        const list = res.data.data.list || [];
                        const map = new Map(list.map(item => [item.id, item]));
                        if (this.parentItemType === 'figure') {
                            const nameId = ids[0];
                            this.boundName = map.get(nameId) || null;
                            const restIds = ids.slice(1);
                            this.boundStories = restIds.map(id => map.get(id)).filter(Boolean);
                        } else {
                            this.boundStories = ids.map(id => map.get(id)).filter(Boolean);
                        }
                        this.loading = false;
                    })
                } catch (e) {
                    this.loading = false;
                    ElMessage.error('获取文本失败，请稍后重试');
                    return;
                }
            }
            this.searchStories();
        },
        searchStories(page = 1) {
            this.skip = (page - 1) * this.limit;
            this.loading = true;
            axios.get('/cms/v1/copywriting', {
                params: {
                    nickname: this.keyword || undefined,
                    skip: this.skip,
                    limit: this.limit,
                },
            })
                .then(res => {
                    this.candidates = res.data.data.list || [];
                    this.total = res.data.data.total || 0;
                    this.loading = false;
                })
                .catch(e => {
                    this.loading = false;
                    ElMessage.error('搜索文本失败，请稍后重试');
                    console.error(e);
                });
        },
        setName(story) {
            if (this.parentItemType !== 'figure') return;
            // 如果该文本在故事列表中，移除避免重复
            this.boundStories = this.boundStories.filter(s => s.id !== story.id);
            this.boundName = { ...story };
        },
        addStory(story) {
            // 去重
            if (this.boundName?.id === story.id || this.boundStories.some(s => s.id === story.id)) {
                ElMessage.warning('该文本已绑定');
                return;
            }
            this.boundStories.push({ ...story });
        },
        unbindStories() {
            if (!this.boundSelected.length) return;
            this.boundStories = this.boundStories.filter(s => !this.boundSelected.includes(s.id));
            this.boundSelected = [];
        },
        save() {
            // 把排序后的 id 列表提交给后端
            let storeIds = this.boundStories.map(s => s.id);
            if (this.parentItemType === 'figure' && this.boundName?.id) {
                storeIds = [this.boundName.id, ...storeIds];
            }
            const type = this.parentItemType;
            const id = this.parentItem?.id;
            if (!id) { ElMessage.error('缺少父级ID'); return; }
            axios.patch(`/cms/v1/${type}/${id}`, {
                    username: this.$root.user.name,
                    stories: storeIds,
                })
                .then(res => {
                    this.parentItem.stories = storeIds;
                    ElMessage.success('保存文本绑定成功');
                    this.close();
                })
                .catch(e => {
                    ElMessage.error('保存失败，请稍后重试');
                    console.error(e);
                });
        },
    },
};
</script>

<style scoped>
.figure-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 12px;
}
</style>